import { MissionBadge } from '@/features/missions/components/MissionBadge'
import type { AudioAnnotationTaskDetail } from '@/features/nectar/api/models'
import { NectarAudioPlayer } from '@/features/nectar/components/shared/NectarAudioPlayer'
import { Trans } from '@lingui/react/macro'
import { clsx } from 'clsx'
import { Check, Lock } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { FIELD_COMPONENTS } from '../audio-annotation/fields'

type Props = {
  index: number
  task: AudioAnnotationTaskDetail
  answers: Record<string, unknown>
  onChange: (key: string, value: unknown) => void
}

export const AnnotatorVerificationTaskItem = ({ index, task, answers, onChange }: Props) => {
  const fields = useMemo(() => task?.formSchema?.fields ?? [], [task?.formSchema?.fields])
  const audioUrl = task?.audioUrl

  const [hasListened, setHasListened] = useState(true)
  const [audioError, setAudioError] = useState(false)

  useEffect(() => {
    setHasListened(false)
    setAudioError(false)
  }, [audioUrl])

  return (
    <div className="flex flex-col items-stretch gap-6">
      <div className="text-body-2 text-gray-70 font-semibold">
        {index + 1}. {task?.instruction}
      </div>
      {task?.phrase && <span className="text-h5 block text-center leading-[120%]">&ldquo;{task.phrase}&rdquo;</span>}
      {task?.topic && (
        <div className="flex justify-center">
          <MissionBadge variant="warning">{task.topic}</MissionBadge>
        </div>
      )}

      <div className="border-b border-solid border-gray-100 py-4">
        {audioUrl ? (
          <NectarAudioPlayer
            audioUrl={audioUrl}
            onFinish={() => setHasListened(true)}
            onError={() => setAudioError(true)}
          />
        ) : (
          <div className="flex h-10 w-full items-center justify-center">
            <div className="text-body-3 text-gray-90">
              <Trans>Audio unavailable</Trans>
            </div>
          </div>
        )}
      </div>

      <div
        className="flex items-center gap-3 rounded-xl border border-solid border-gray-100 bg-gray-100/30 px-4 py-3"
        role="status"
        aria-live="polite"
      >
        {hasListened ? (
          <>
            <Check className="text-green h-4 w-4 shrink-0" />
            <div className="text-body-3 text-gray-70">
              <Trans>Audio played — you can answer now.</Trans>
            </div>
          </>
        ) : (
          <>
            <Lock className="text-gray-70 h-4 w-4 shrink-0" />
            <div className="text-body-3 text-gray-70">
              {!audioUrl || audioError ? (
                <Trans>Audio could not be loaded — you can’t annotate this task.</Trans>
              ) : (
                <Trans>Listen to the audio to the end before answering.</Trans>
              )}
            </div>
          </>
        )}
      </div>

      <fieldset
        disabled={!hasListened}
        aria-disabled={!hasListened}
        className={clsx(
          'm-0 flex min-w-0 flex-col gap-6 border-0 p-0 transition-opacity',
          !hasListened && 'pointer-events-none opacity-50'
        )}
      >
        {fields.map((field) => {
          const Component = FIELD_COMPONENTS.get(field.type)
          if (!Component) return null
          return (
            <Component
              key={field.key}
              field={field}
              value={answers[field.key]}
              onChange={(value) => onChange(field.key, value)}
            />
          )
        })}
      </fieldset>
    </div>
  )
}
